import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import { pnrRegex, ticketNumberRegex } from 'utils/validators';
import { ADJUST_POINTS_TYPE, ADD_POINTS_REASONS, DEDUCT_POINTS_REASONS, MAX_ADD_POINTS_AMOUNT } from 'constants/clubEurostar';
import { ConfirmButton, CancelButton } from 'components/ui/Button';
import { useLazyQuery } from '@apollo/client';

import {
  PointsInput,
  MandatoryFieldLabel,
  PointsLabel,
  AdjustPointsReasonSelect,
  AdjustPointsReasonTextArea,
  ActionsList,
  Error,
  PointsFormField
} from '../../ClubEurostarMember.styles';
import { GET_ADJUSTMENT_REASONS } from '../graphql';
import Label from 'components/ui/Label';

const AdjustPointsForm = ({
  intl: { formatMessage },
  type = ADJUST_POINTS_TYPE.ADD,
  onSubmit,
  onCancel,
  rewardPoints,
  statusPoints,
  loading
}) => {
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    watch
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      pnr: '',
      tcn: '',
      rewardPoints: 0,
      acceleratorPoints: 0,
      statusPoints: 0,
      reasonCode: '',
      reasonText: ''
    }
  });

  const maxRewardPoints = type === ADJUST_POINTS_TYPE.ADD ? MAX_ADD_POINTS_AMOUNT : rewardPoints;
  const maxStatusPoints = type === ADJUST_POINTS_TYPE.ADD ? MAX_ADD_POINTS_AMOUNT : statusPoints;

  const [queryAdjustmentReasons, { data: bookingData }] = useLazyQuery(GET_ADJUSTMENT_REASONS);

  useEffect(() => {
    queryAdjustmentReasons();
  }, [queryAdjustmentReasons]);

  const adjustPointsReasons = useMemo(() => {
    if (type !== ADJUST_POINTS_TYPE.ADD) {
      return DEDUCT_POINTS_REASONS;
    }

    if (!bookingData) {
      return ADD_POINTS_REASONS;
    }

    const reasons = bookingData?.loyaltyPointsAdjustmentReasons.map((reason) => ({
      value: reason.code,
      label: reason.description
    }));

    return reasons || [];
  }, [type, bookingData]);

  const validateAtLeastOnePoints = (value, allValues) => {
    const rewardPoints = parseFloat(watch('rewardPoints')) || 0;
    const acceleratorPoints = parseFloat(watch('acceleratorPoints')) || 0;
    const statusPoints = parseFloat(watch('statusPoints')) || 0;

    return rewardPoints > 0 || acceleratorPoints > 0 || statusPoints > 0;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-cy="adjust-points-form" data-testid="adjust-points-form">
      <PointsFormField>
        <MandatoryFieldLabel htmlFor="pnr">
          <FormattedMessage id="clubEurostar.associatedPNR" />
        </MandatoryFieldLabel>
        <div>
          <PointsInput {...register('pnr', { required: true, pattern: pnrRegex })} type="text" id="pnr" />
        </div>
        {errors.pnr && (
          <Error data-cy="error">
            <FormattedMessage id="clubEurostar.pnrValidationMessage" />
          </Error>
        )}
      </PointsFormField>

      <PointsFormField>
        <Label htmlFor="tcn">
          <FormattedMessage id="clubEurostar.associatedTCN" />
        </Label>
        <div>
          <PointsInput {...register('tcn', { required: false, pattern: ticketNumberRegex })} type="text" id="tcn" />
        </div>
        {errors.tcn && (
          <Error data-cy="error">
            <FormattedMessage id="clubEurostar.tcnValidationMessage" />
          </Error>
        )}
      </PointsFormField>

      <PointsFormField>
        <MandatoryFieldLabel htmlFor="rewardPoints">
          <FormattedMessage id={type === ADJUST_POINTS_TYPE.ADD ? 'clubEurostar.pointsToBeAdded' : 'clubEurostar.pointsToBeDeducted'} />
        </MandatoryFieldLabel>
        <div>
          <PointsInput
            {...register('rewardPoints', {
              required: true,
              min: 1,
              max: maxRewardPoints,
              pattern: /^\d+$/
            })}
            type="number"
            step="1"
            id="rewardPoints"
          />{' '}
          <PointsLabel>
            <FormattedMessage id="clubEurostar.points" />
          </PointsLabel>
        </div>
      </PointsFormField>

      <PointsFormField>
        <div>
          <PointsInput
            {...register('acceleratorPoints', {
              required: false,
              min: 0,
              max: maxRewardPoints,
              pattern: /^\d+$/,
              validate: validateAtLeastOnePoints
            })}
            type="number"
            step="1"
            id="acceleratorPoints"
          />{' '}
          <PointsLabel>
            <FormattedMessage id="clubEurostar.acceleratorPoints" />
          </PointsLabel>
        </div>
      </PointsFormField>

      <PointsFormField>
        <div>
          <PointsInput
            {...register('statusPoints', {
              required: false,
              min: 0,
              max: maxStatusPoints,
              pattern: /^\d+$/,
              validate: validateAtLeastOnePoints
            })}
            type="number"
            step="1"
            id="statusPoints"
          />{' '}
          <PointsLabel>
            <FormattedMessage id="clubEurostar.statusPoints" />
          </PointsLabel>
        </div>
      </PointsFormField>

      {(errors.rewardPoints || errors.acceleratorPoints || errors.statusPoints) && (
        <Error data-cy="error">
          <FormattedMessage id="clubEurostar.pointsValidationMessage" values={{ max: maxStatusPoints }} />
        </Error>
      )}

      <PointsFormField>
        <MandatoryFieldLabel htmlFor="reasonCode">
          <FormattedMessage id="clubEurostar.reason" />
        </MandatoryFieldLabel>
        <div>
          <AdjustPointsReasonSelect {...register('reasonCode', { required: true })} id="reasonCode">
            <option key="" value="">
              {formatMessage({ id: 'clubEurostar.selectReason' })}
            </option>
            {adjustPointsReasons.map((reason) => (
              <option key={reason.value} value={reason.value}>
                {reason.label}
              </option>
            ))}
          </AdjustPointsReasonSelect>
        </div>
        {errors.reasonCode && (
          <Error data-cy="error">
            <FormattedMessage id="clubEurostar.selectReason" />
          </Error>
        )}
      </PointsFormField>
      <PointsFormField>
        <MandatoryFieldLabel htmlFor="reasonText">
          <FormattedMessage id="clubEurostar.reasonText" />
        </MandatoryFieldLabel>
        <div>
          <AdjustPointsReasonTextArea {...register('reasonText', { required: true, minLength: 5 })} type="text" id="reasonText" rows="3" />
        </div>
        {errors.reasonText && (
          <Error data-cy="error">
            <FormattedMessage id="clubEurostar.reasonTextValidationMessage" />
          </Error>
        )}
      </PointsFormField>
      <ActionsList>
        <CancelButton type="button" data-cy="adjust-points-cancel-button" data-testid="adjust-points-cancel-button" onClick={onCancel}>
          <FormattedMessage id="clubEurostar.cancel" />
        </CancelButton>
        <ConfirmButton
          type="submit"
          data-cy="adjust-points-confirm-button"
          data-testid="adjust-points-confirm-button"
          disabled={!isValid || loading}
          loading={loading}
        >
          <FormattedMessage id="clubEurostar.confirm" />
        </ConfirmButton>
      </ActionsList>
    </form>
  );
};

AdjustPointsForm.propTypes = {
  type: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  rewardPoints: PropTypes.number,
  statusPoints: PropTypes.number,
  loading: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired
};

export default AdjustPointsForm;
